import { Component, computed, inject, signal } from '@angular/core';
import {
  ButtonType,
  ColumnConfig,
  CustomAction,
  FieldType,
  GenericTableComponent,
  GlobalAction,
  HeaderConfigurationService,
  ModalMode,
  ModalTabConfig,
  TableAction,
  TableActionConfig,
} from '@solcre-org/core-ui';
import { environment } from '../../../../environments/environment.local';
import { VesselModel } from '../../../shared/models/vessel.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TypeOfVessels } from '../../../shared/enums/type-of-vessels.enum';
import { VesselRampType } from '../../../shared/enums/vessel-ramp-types';
import { Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NUMERIC_PATTERN_VALIDATOR } from '../../../shared/validators/pattern-validators';

@Component({
  selector: 'app-vessels',
  imports: [GenericTableComponent, TranslateModule],
  templateUrl: './vessels.html',
})
export class Vessels {
  private headerConfigurationService = inject(HeaderConfigurationService);
  private translateService: TranslateService = inject(TranslateService);
  private router = inject(Router);
  endpoint = environment.API.VESSELS_URI;
  vessels = signal<VesselModel[]>([]);
  optionsStatus = signal<StatusOption[]>([
    {
      value: 'active',
      label: 'Active',
    },
    {
      value: 'inactive',
      label: 'Inactive',
    },
    {
      value: 'maintenance',
      label: 'Maintenance',
    },
    {
      value: 'out_of_service',
      label: 'Out of Service',
    },
  ]);

  columns: ColumnConfig<VesselModel>[] = [
    {
      label: 'vessels.vesselName',
      key: 'vesselName',
    },
    {
      label: 'vessels.imoNumber',
      key: 'imoNumber',
    },
    {
      label: 'vessels.typeOfVessel',
      key: 'typeOfVessel',
    },
    {
      label: 'vessels.totalSquareMeters',
      key: 'totalSquareMeters',
    },
    {
      label: 'vessels.totalLaneMeters',
      key: 'totalLaneMeters',
    },
    {
      label: 'vessels.totalCapacityVehicles',
      key: 'totalCapacityVehicles',
    },
    {
      label: 'vessels.totalCapacityTons',
      key: 'totalCapacityTons',
    },
    {
      label: 'vessels.status',
      key: 'status',
    },
  ];

  modalFields = computed<ModalTabConfig<VesselModel>[]>(
    () =>
      [
        {
          tabId: 'general_data',
          tabLabel: 'vessels.general_data',
          fields: [
            {
              label: 'vessels.vesselName',
              placeholder: 'vessels.vesselNamePlaceholder',
              key: 'vesselName',
              type: FieldType.TEXT,
              readonly: false,
              validators: [Validators.required],
              errorMessages: {
                required: this.translateService.instant('validators.required'),
              },
            },
            {
              label: 'vessels.imoNumber',
              placeholder: 'vessels.imoNumberPlaceholder',
              key: 'imoNumber',
              type: FieldType.TEXT,
              readonly: false,
              validators: [
                Validators.required,
                Validators.minLength(7),
                Validators.maxLength(7),
                NUMERIC_PATTERN_VALIDATOR
              ],
              errorMessages: {
                required: this.translateService.instant('validators.required'),
                maxlength: this.translateService.instant(
                  'validators.maxLength',
                  { maxLength: 7 }
                ),
                minlength: this.translateService.instant(
                  'validators.minLength',
                  { minLength: 7 }
                ),
                pattern: this.translateService.instant('validators.numericPattern'),
              },
            },
            {
              label: 'vessels.typeOfVessel',
              placeholder: 'vessels.typeOfVesselPlaceholder',
              key: 'typeOfVessel',
              type: FieldType.SELECT,
              searchable: true,
              defaultValue: null,
              options: [
                {
                  value: TypeOfVessels.TYPE_BULK,
                  label: 'Bulk',
                },
                {
                  value: TypeOfVessels.TYPE_CONTAINER,
                  label: 'Container',
                },
                {
                  value: TypeOfVessels.TYPE_GENERAL_CARGO,
                  label: 'General Cargo',
                },
                {
                  value: TypeOfVessels.TYPE_MULTIPURPOSE,
                  label: 'Multipurpose',
                },
                {
                  value: TypeOfVessels.TYPE_RORO,
                  label: 'Roro',
                },
                {
                  value: TypeOfVessels.TYPE_TANKER,
                  label: 'Tanker',
                },
              ],
              readonly: false,
              validators: [Validators.required],
              errorMessages: {
                required: this.translateService.instant('validators.required'),
              },
            },
            {
              label: 'vessels.status',
              placeholder: 'vessels.statusPlaceholder',
              key: 'status',
              type: FieldType.SELECT,
              searchable: true,
              defaultValue: '',
              options: this.optionsStatus(),
              readonly: false,
              validators: [Validators.required],
              errorMessages: {
                required: this.translateService.instant('validators.required'),
              },
            },
            {
              label: 'vessels.totalSquareMeters',
              placeholder: 'vessels.totalSquareMetersPlaceholder',
              key: 'totalSquareMeters',
              defaultValue: '',
              type: FieldType.NUMBER,
              readonly: false,
              validators: [Validators.required, Validators.min(1)],
              errorMessages: {
                required: this.translateService.instant('validators.required'),
                min: this.translateService.instant('validators.min', { min: 1 }),
              },
            },
            {
              label: 'vessels.totalLaneMeters',
              placeholder: 'vessels.totalLaneMetersPlaceholder',
              key: 'totalLaneMeters',
              defaultValue: '',
              type: FieldType.NUMBER,
              readonly: false,
              validators: [Validators.required, Validators.min(1)],
              errorMessages: {
                required: this.translateService.instant('validators.required'),
                min: this.translateService.instant('validators.min', { min: 1 }),
              },
            },
            {
              label: 'vessels.totalCapacityVehicles',
              placeholder: 'vessels.totalCapacityVehiclesPlaceholder',
              key: 'totalCapacityVehicles',
              defaultValue: '',
              type: FieldType.NUMBER,
              readonly: false,
            },
            {
              label: 'vessels.totalCapacityTons',
              placeholder: 'vessels.totalCapacityTonsPlaceholder',
              key: 'totalCapacityTons',
              defaultValue: '',
              type: FieldType.NUMBER,
              readonly: false,
            },
            {
              label: 'vessels.vesselCostPerDay',
              placeholder: 'vessels.vesselCostPerDayPlaceholder',
              key: 'vesselCostPerDay',
              defaultValue: '',
              type: FieldType.NUMBER,
              readonly: false,
            },
            {
              label: 'vessels.mafiCapacity',
              placeholder: 'vessels.mafiCapacityPlaceholder',
              key: 'mafiCapacity',
              defaultValue: '',
              type: FieldType.NUMBER,
              readonly: false,
            },
            {
              label: 'vessels.airDraft',
              placeholder: 'vessels.airDraftPlaceholder',
              key: 'airDraft',
              defaultValue: '',
              type: FieldType.NUMBER,
              readonly: false,
            },
            {
              label: 'vessels.rampType',
              placeholder: 'vessels.rampTypePlaceholder',
              key: 'rampType',
              type: FieldType.SELECT,
              searchable: true,
              multiple: true,
              defaultValue: [],
              options: [
                {
                  value: VesselRampType.STERN_RAMP,
                  label: this.translateService.instant('vessels.ramps.sternRamp'),
                },
                {
                  value: VesselRampType.SIDE_RAMP,
                  label: this.translateService.instant('vessels.ramps.sideRamp'),
                },
                {
                  value: VesselRampType.QUARTER_RAMP,
                  label: this.translateService.instant('vessels.ramps.quarterRamp'),
                },
              ],
              readonly: false,
            },
            {
              label: 'currency.createdAt',
              key: 'createdAt',
              type: FieldType.DATE,
              readonly: true,
              modes: {
                [ModalMode.CREATE]: { visible: false, includeInPayload: false },
                [ModalMode.EDIT]: { visible: false, includeInPayload: false },
                [ModalMode.VIEW]: { visible: true, includeInPayload: false },
              },
            },
          ],
        },
        {
          tabId: 'technical_data',
          tabLabel: 'vessels.technical_data',
          fields: [
            {
              label: 'vessels.length',
              placeholder: 'vessels.lengthPlaceholder',
              key: 'loa',
              defaultValue: '',
              type: FieldType.NUMBER,
              readonly: false,
            },
            {
              label: 'vessels.beam',
              placeholder: 'vessels.beamPlaceholder',
              key: 'beam',
              defaultValue: '',
              type: FieldType.NUMBER,
              readonly: false,
            },
            {
              label: 'vessels.grossTonnage',
              placeholder: 'vessels.grossTonnagePlaceholder',
              key: 'grt',
              defaultValue: '',
              type: FieldType.NUMBER,
              readonly: false,
            },
            {
              label: 'vessels.netTonnage',
              placeholder: 'vessels.netTonnagePlaceholder',
              key: 'nrt',
              defaultValue: '',
              type: FieldType.NUMBER,
              readonly: false,
            },
            {
              label: 'vessels.deadweight',
              placeholder: 'vessels.deadweightPlaceholder',
              key: 'dwt',
              defaultValue: '',
              type: FieldType.NUMBER,
              readonly: false,
            },
          ],
        },
      ] as any as ModalTabConfig<VesselModel>[]
  );

  globalActions: GlobalAction<VesselModel>[] = [];
  actions: TableActionConfig[] = [
    { action: TableAction.VIEW, isExtra: false },
    { action: TableAction.EDIT, isExtra: false },
    { action: TableAction.DELETE, isExtra: true },
    { action: TableAction.SELECT, isExtra: false },
    { action: TableAction.CREATE, isExtra: false },
  ];

  ngOnInit() {
    this.headerConfigurationService.setEndpointHeaders(this.endpoint, {
      patch: [{ 'Content-Type': 'application/merge-patch+json' }],
    });
  }

  vesselsModelFactory = (data: Partial<VesselModel>): VesselModel => {
    return new VesselModel().fromJSON(data);
  };

  customActions: CustomAction<VesselModel>[] = [
    {
      buttonConfig: {
        type: ButtonType.ICON,
        icon: 'fa-border-none',
      },
      isExtra: false,
      tooltipPosition: 'top',
      title: this.translateService.instant('vessels.decks.title'),
      callback: (row: VesselModel) => this.navigateToDecks(row),
    },
    {
      buttonConfig: {
        type: ButtonType.ICON,
        icon: 'fa-gas-pump',
      },
      isExtra: false,
      tooltipPosition: 'top',
      title: this.translateService.instant('vessels.fuel.title'),
      callback: (row: VesselModel) => this.navigateToFuel(row),
    },
  ];

  navigateToDecks(vessel: VesselModel): void {
    if (vessel.id) {
      this.router.navigate(['app/vessels/' + vessel.id + '/decks']);
    }
  }

  navigateToFuel(vessel: VesselModel): void {
    if (vessel.id) {
      this.router.navigate(['app/vessels/' + vessel.id + '/fuel']);
    }
  }
}
